import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import checkIcon from 'browser/assets/icons/check.svg';
import PropTypes from 'prop-types';
import { LangContext } from 'browser/contexts/LangContext';
import { TERMS } from 'components/Contact/Terms';
import ModalContext from 'contexts/ModalContext';
import { useLocalization } from 'gatsby-theme-i18n';

export const Label = styled.label`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 30px;
  margin: 10px 0;
  cursor: pointer;
  font-size: 1.2rem;
  text-align: left;
  &:first-of-type {
    margin-bottom: 13px;
  }
`;

const LabelText = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  word-break: break-all;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  ${({ expanded }) =>
    expanded &&
    css`
      display: block;
      text-overflow: clip;
      -webkit-line-clamp: none;
      word-break: normal;
    `}
  button {
    display: inline-block;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.primary};
    padding: 0;
    margin: 0;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const Checkmark = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  height: ${({ big }) => (big ? '25px' : '20px')};
  width: ${({ big }) => (big ? '25px' : '20px')};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid;
  transition: 0.2s;
  border-color: ${({ theme, error }) =>
    error ? theme.colors.red : theme.colors.gray100};
  &:after {
    content: '';
    position: absolute;
    opacity: 0;
    transition: 0.2s;
    left: ${({ big }) => (big ? '6px' : '3px')};
    top: ${({ big }) => (big ? '5px' : '3px')};
    width: 12px;
    height: 12px;
    background: url(${checkIcon}) no-repeat center;
  }
`;

export const Checkbox = styled.input`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
  &:checked ~ ${Checkmark} {
    background: ${({ theme }) => theme.colors.primaryLight};
    border-color: ${({ theme }) => theme.colors.primaryLight};
    &:after {
      opacity: 1;
    }
  }
`;

const StyledLabelText = styled(LabelText)`
  font-weight: 500;
`;

const Terms = ({ setFieldValue, values, setErrors, errors, submitClicked }) => {
  const { showModal } = useContext(ModalContext);
  const { translate } = useContext(LangContext);
  const { locale } = useLocalization();

  const finalTerms = TERMS[locale].map(
    ({ formTermsText, privacyPolicy }, index) => ({
      name: `agree${index + 1}Browser`,
      content: formTermsText,
      policy: privacyPolicy,
      required: index === 0,
    })
  );

  // const initExpandedTerms =
  //   typeof window !== `undefined` && window.innerWidth >= 440
  //     ? []
  //     : finalTerms.map(({ name }) => name);
  // const [expandedTerms, setExpandedTerms] = useState(initExpandedTerms);

  // const handleCheckbox = name => {
  //   if (expandedTerms.includes(name)) {
  //     setExpandedTerms(expandedTerms.filter(term => term !== name));
  //   } else setExpandedTerms([...expandedTerms, name]);
  // };

  const acceptAllKey = 'agreeAllBrowser';
  const agree1Key = 'agree1Browser';

  return (
    <>
      {finalTerms.length > 2 && (
        <Label htmlFor={acceptAllKey}>
          <StyledLabelText>{translate('acceptAll')}</StyledLabelText>
          <Checkbox
            id={acceptAllKey}
            name={acceptAllKey}
            type="checkbox"
            onChange={() => {
              if (!finalTerms.every(term => values[term.name] === true)) {
                setFieldValue(acceptAllKey, true);
                finalTerms.forEach(term => setFieldValue(term.name, true));
              } else {
                setFieldValue(acceptAllKey, true);
                finalTerms.forEach(term => setFieldValue(term.name, false));
              }
            }}
            checked={finalTerms.every(term => values[term.name] === true)}
          />
          <Checkmark />
        </Label>
      )}
      {finalTerms.map(({ name, content, policy, required }) => (
        <Label htmlFor={name} key={name}>
          <LabelText expanded>
            {content}{' '}
            {policy && (
              <button onClick={showModal} type="button">
                {policy}
              </button>
            )}
          </LabelText>
          <Checkbox
            id={name}
            type="checkbox"
            onChange={() => {
              setFieldValue(name, !values[name]);
              required && setErrors({ ...errors, [agree1Key]: '' });
            }}
            checked={values[name]}
          />
          <Checkmark
            error={
              submitClicked &&
              errors[agree1Key] &&
              required &&
              values[name] === false
            }
          />
        </Label>
      ))}
    </>
  );
};

Terms.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  ).isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.string).isRequired,
  submitClicked: PropTypes.bool.isRequired,
};

export default Terms;
